import React, { useState } from 'react';
import { EditIcon, SaveIcon, XIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, CreditCardIcon } from 'lucide-react';
interface DemographicsFormProps {
  patientId: number;
}
export function DemographicsForm({
  patientId
}: DemographicsFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    dateOfBirth: '1979-05-15',
    gender: 'Male',
    bloodGroup: 'O+',
    email: 'john.doe@email.com',
    phone: '+1 (555) 123-4567',
    alternatePhone: '+1 (555) 987-6543',
    address: '123 Main Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'USA',
    emergencyContactName: 'Jane Doe',
    emergencyContactPhone: '+1 (555) 111-2222',
    emergencyContactRelation: 'Spouse',
    insuranceProvider: 'Blue Cross Blue Shield',
    insurancePolicyNumber: 'BC123456789',
    insuranceGroupNumber: 'GRP001',
    insuranceValidUntil: '2025-12-31'
  });
  const handleSave = () => {
    setIsEditing(false);
    alert('Patient information updated successfully!');
  };
  return <div className="space-y-6" data-id="element-702">
      {/* Header with Edit Button */}
      <div className="flex items-center justify-between" data-id="element-703">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-704">Patient Information</h2>
        {!isEditing ? <button onClick={() => setIsEditing(true)} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-705">
            <EditIcon className="w-4 h-4" data-id="element-706" />
            <span data-id="element-707">Edit</span>
          </button> : <div className="flex space-x-2" data-id="element-708">
            <button onClick={() => setIsEditing(false)} className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-id="element-709">
              <XIcon className="w-4 h-4" data-id="element-710" />
              <span data-id="element-711">Cancel</span>
            </button>
            <button onClick={handleSave} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-712">
              <SaveIcon className="w-4 h-4" data-id="element-713" />
              <span data-id="element-714">Save</span>
            </button>
          </div>}
      </div>
      {/* Personal Information */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-715">
        <h3 className="flex items-center space-x-2 text-lg font-semibold text-gray-900 mb-4" data-id="element-716">
          <UserIcon className="w-5 h-5 text-green-600" data-id="element-717" />
          <span data-id="element-718">Personal Information</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-719">
          <div data-id="element-720">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-721">
              First Name
            </label>
            <input type="text" value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-722" />
          </div>
          <div data-id="element-723">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-724">
              Last Name
            </label>
            <input type="text" value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-725" />
          </div>
          <div data-id="element-726">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-727">
              Date of Birth
            </label>
            <input type="date" value={formData.dateOfBirth} onChange={e => setFormData({
            ...formData,
            dateOfBirth: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-728" />
          </div>
          <div data-id="element-729">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-730">
              Gender
            </label>
            <select value={formData.gender} onChange={e => setFormData({
            ...formData,
            gender: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-731">
              <option value="Male" data-id="element-732">Male</option>
              <option value="Female" data-id="element-733">Female</option>
              <option value="Other" data-id="element-734">Other</option>
            </select>
          </div>
          <div data-id="element-735">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-736">
              Blood Group
            </label>
            <select value={formData.bloodGroup} onChange={e => setFormData({
            ...formData,
            bloodGroup: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-737">
              <option value="A+" data-id="element-738">A+</option>
              <option value="A-" data-id="element-739">A-</option>
              <option value="B+" data-id="element-740">B+</option>
              <option value="B-" data-id="element-741">B-</option>
              <option value="AB+" data-id="element-742">AB+</option>
              <option value="AB-" data-id="element-743">AB-</option>
              <option value="O+" data-id="element-744">O+</option>
              <option value="O-" data-id="element-745">O-</option>
            </select>
          </div>
        </div>
      </div>
      {/* Contact Information */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-746">
        <h3 className="flex items-center space-x-2 text-lg font-semibold text-gray-900 mb-4" data-id="element-747">
          <PhoneIcon className="w-5 h-5 text-green-600" data-id="element-748" />
          <span data-id="element-749">Contact Information</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-750">
          <div data-id="element-751">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-752">
              Email
            </label>
            <input type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-753" />
          </div>
          <div data-id="element-754">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-755">
              Phone
            </label>
            <input type="tel" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-756" />
          </div>
          <div className="md:col-span-2" data-id="element-757">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-758">
              Alternate Phone
            </label>
            <input type="tel" value={formData.alternatePhone} onChange={e => setFormData({
            ...formData,
            alternatePhone: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-759" />
          </div>
        </div>
      </div>
      {/* Address */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-760">
        <h3 className="flex items-center space-x-2 text-lg font-semibold text-gray-900 mb-4" data-id="element-761">
          <MapPinIcon className="w-5 h-5 text-green-600" data-id="element-762" />
          <span data-id="element-763">Address</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-764">
          <div className="md:col-span-2" data-id="element-765">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-766">
              Street Address
            </label>
            <input type="text" value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-767" />
          </div>
          <div data-id="element-768">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-769">
              City
            </label>
            <input type="text" value={formData.city} onChange={e => setFormData({
            ...formData,
            city: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-770" />
          </div>
          <div data-id="element-771">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-772">
              State
            </label>
            <input type="text" value={formData.state} onChange={e => setFormData({
            ...formData,
            state: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-773" />
          </div>
          <div data-id="element-774">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-775">
              ZIP Code
            </label>
            <input type="text" value={formData.zipCode} onChange={e => setFormData({
            ...formData,
            zipCode: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-776" />
          </div>
          <div data-id="element-777">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-778">
              Country
            </label>
            <input type="text" value={formData.country} onChange={e => setFormData({
            ...formData,
            country: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-779" />
          </div>
        </div>
      </div>
      {/* Emergency Contact */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-780">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-781">
          Emergency Contact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-782">
          <div data-id="element-783">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-784">
              Name
            </label>
            <input type="text" value={formData.emergencyContactName} onChange={e => setFormData({
            ...formData,
            emergencyContactName: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-785" />
          </div>
          <div data-id="element-786">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-787">
              Phone
            </label>
            <input type="tel" value={formData.emergencyContactPhone} onChange={e => setFormData({
            ...formData,
            emergencyContactPhone: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-788" />
          </div>
          <div data-id="element-789">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-790">
              Relation
            </label>
            <input type="text" value={formData.emergencyContactRelation} onChange={e => setFormData({
            ...formData,
            emergencyContactRelation: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-791" />
          </div>
        </div>
      </div>
      {/* Insurance Information */}
      <div className="border border-gray-200 rounded-lg p-6" data-id="element-792">
        <h3 className="flex items-center space-x-2 text-lg font-semibold text-gray-900 mb-4" data-id="element-793">
          <CreditCardIcon className="w-5 h-5 text-green-600" data-id="element-794" />
          <span data-id="element-795">Insurance Information</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-796">
          <div data-id="element-797">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-798">
              Insurance Provider
            </label>
            <input type="text" value={formData.insuranceProvider} onChange={e => setFormData({
            ...formData,
            insuranceProvider: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-799" />
          </div>
          <div data-id="element-800">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-801">
              Policy Number
            </label>
            <input type="text" value={formData.insurancePolicyNumber} onChange={e => setFormData({
            ...formData,
            insurancePolicyNumber: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-802" />
          </div>
          <div data-id="element-803">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-804">
              Group Number
            </label>
            <input type="text" value={formData.insuranceGroupNumber} onChange={e => setFormData({
            ...formData,
            insuranceGroupNumber: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-805" />
          </div>
          <div data-id="element-806">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-807">
              Valid Until
            </label>
            <input type="date" value={formData.insuranceValidUntil} onChange={e => setFormData({
            ...formData,
            insuranceValidUntil: e.target.value
          })} disabled={!isEditing} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50" data-id="element-808" />
          </div>
        </div>
      </div>
    </div>;
}